import { useEffect, useRef } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import {
	Dialog,
	DialogSurface,
	DialogTitle,
	DialogBody,
	Button,
	Textarea,
	Text,
	DialogActions,
	DialogTrigger,
	Field,
	mergeClasses,
} from "@fluentui/react-components";
import { Flex } from "../flex";
import { DialogContent } from "@fluentui/react";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { FormTextField } from "@/components/molecules/formTextField/FormTextField";
import {
	feedbackMaxDescriptionLength,
	feedbackMaxFieldsLength,
	feedbackScreenshotSizeLimit,
	feedbackScreenshotSizeLimitMB,
	formValidationRequiredKey,
	RegEx,
} from "@/common/constants";
import { FeedbackFormProps, FeedbackFormValues } from "./types";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

const FeedbackForm = ({
	onClose,
	onSubmit,
	initialValues,
}: FeedbackFormProps) => {
	const intl = useIntl();

	const classes = useStyle();

	const formMethods = useForm<FeedbackFormValues>({
		mode: "all",
		reValidateMode: "onChange",
		defaultValues: {
			email: initialValues.email,
			name: initialValues.name,
		},
	});

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const {
		formState: { errors, isValid },
		trigger,
		getValues,
		register,
		unregister,
		reset,
	} = formMethods;

	const { isMobile } = useResponsiveBreakpoints();

	useEffect(() => {
		register("description", {
			validate: (value) => {
				return (
					(value && value.length > 0) ||
					intl.formatMessage({
						id: formValidationRequiredKey,
					})
				);
			},
		});

		return () => {
			unregister("description");
			reset();
		};
	}, []);

	const onSubmitForm = (_: any) => {
		trigger();
		if (isValid) {
			const contactFormValues = getValues();
			onSubmit(contactFormValues);
		}
	};

	return (
		<Dialog open>
			<DialogSurface>
				<FormProvider {...formMethods}>
					<DialogBody>
						<DialogTitle>
							{intl.formatMessage({
								id: "Feedback.Form.Title",
							})}
							<br />
							<Text>
								{intl.formatMessage({
									id: "Feedback.Form.Subtitle",
								})}
							</Text>
						</DialogTitle>
						<DialogContent className={classes.dialogContent}>
							<form>
								<Flex column>
									<FormTextField
										propertyName="name"
										translationKey="Feedback.Form.Name"
										maxLength={feedbackMaxFieldsLength}
										validationRules={{
											required: {
												value: true,
												message: intl.formatMessage({
													id: formValidationRequiredKey,
												}),
											},
										}}
									/>
									<FormTextField
										propertyName="email"
										translationKey="Feedback.Form.Email"
										maxLength={feedbackMaxFieldsLength}
										pattern={RegEx.Email}
										validationRules={{
											required: {
												value: true,
												message: intl.formatMessage({
													id: formValidationRequiredKey,
												}),
											},
										}}
									/>
									<Controller
										name="description"
										defaultValue=""
										rules={{
											maxLength: {
												value: feedbackMaxDescriptionLength,
												message: intl.formatMessage(
													{ id: "Common.Validations.MaxLength" },
													{ maxLength: feedbackMaxDescriptionLength }
												),
											},
										}}
										render={({ field: { onChange, onBlur, value } }) => (
											<Field
												label={intl.formatMessage({
													id: "Feedback.Form.Description",
												})}
												required
												validationMessage={errors.description?.message}
												validationState={errors.description ? "error" : "none"}
											>
												<Textarea
													size="large"
													appearance="filled-lighter"
													className={mergeClasses(
														classes.descriptionField,
														isMobile && classes.descriptionFieldMobile
													)}
													value={value}
													onChange={(_, data) => onChange(data.value)}
													onBlur={onBlur}
												/>
											</Field>
										)}
									/>
									<Controller
										name="screenshot"
										defaultValue={null}
										rules={{
											validate: {
												fileSize: (value) => {
													if (
														value &&
														value?.size > feedbackScreenshotSizeLimit
													) {
														return intl.formatMessage(
															{
																id: "Feedback.Form.MaxFileSize",
															},
															{ fileSizeLimit: feedbackScreenshotSizeLimitMB }
														);
													}

													return true;
												},
											},
										}}
										render={({ field: { onChange, onBlur } }) => (
											<Field
												validationMessage={errors.screenshot?.message}
												validationState={errors.screenshot ? "error" : "none"}
												className={classes.inputField}
											>
												<Flex column vAlign="center">
													<Button
														className={classes.inputButton}
														onClick={() => fileInputRef.current?.click()}
														appearance="primary"
													>
														{intl.formatMessage({
															id: "Feedback.Form.UploadScreenshot",
														})}
													</Button>
													<Text>{getValues("screenshot")?.name}</Text>
												</Flex>
												<input
													className={classes.fileInput}
													ref={fileInputRef}
													type="file"
													accept=".jpg, .jpeg, .png"
													onChange={(e) => {
														onChange(e.target.files?.[0]);
													}}
													onBlur={onBlur}
												/>
											</Field>
										)}
									/>
								</Flex>
							</form>
						</DialogContent>
						<DialogActions className={classes.dialogActions}>
							<Button appearance="primary" onClick={onSubmitForm}>
								{intl.formatMessage({
									id: "Feedback.Form.Submit",
								})}
							</Button>
							<DialogTrigger disableButtonEnhancement>
								<Button appearance="secondary" onClick={onClose}>
									{intl.formatMessage({
										id: "Feedback.Form.Close",
									})}
								</Button>
							</DialogTrigger>
						</DialogActions>
					</DialogBody>
				</FormProvider>
			</DialogSurface>
		</Dialog>
	);
};

export default FeedbackForm;
